import { ChangeDetectorRef, Component, OnInit, signal } from "@angular/core";
import { BiTranslateService } from "@globals/bi-translate";
import { BiPhoneFormatTool } from "@globals/classes/BiPhoneFormatTool";
import { CustomAnimations } from "@globals/classes/CustomAnimations";
import { isStringNullOrEmpty } from "@globals/helper-functions";
import { SubscriptionType } from "@globals/openapi-models/model/subscriptionType";
import { BiDialogBaseService } from "@globals/services/bi-dialog-base.service";
import { BiSearchBaseService } from "@globals/services/bi-search-base.service";
import { SubscribeModuleBaseService } from "@globals/services/bi-subscribe-module-base.service";
import { BiNotificationConfig, BiToastNotificationService, NotificationType } from "@globals/services/bi-toast-notification.service";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { filter, map, shareReplay, take } from "rxjs";
import { SubscriptionModuleBase } from "../shared/SubscriptionModuleBase";
import { CreateSubscriptionEventData } from "./create-address-subscription/create-address-subscription.component";
import { SupplyNumberAndAlias } from "./create-supply-number-subscription/create-supply-number-subscription.component";
import { SubscriptionModuleSharedService } from "./subscription-module-shared.service";
import { UnsubscribeEventData } from "./subscription-overview/subscription-overview.component";

@UntilDestroy()
@Component({
  selector: "iframe-subscription",
  templateUrl: "./iframe-subscription.component.html",
  styleUrls: ["../shared/subscription-module-styles.scss"],
  providers: [BiSearchBaseService],
  animations: [CustomAnimations.fadeInOut, CustomAnimations.fadeIn],
  standalone: false
})
export class IframeSubscriptionComponent extends SubscriptionModuleBase implements OnInit {
  protected isNameRequired = false;

  /**
   * As we don't know if there are email and/or landline input, we don't know what error to display.
   * This is determined dynamically and message is saved in this variable.
   */
  public atLeast1RequiredMessage: string;

  public title = signal<string>(undefined);
  public showDefaultTitle = false;
  public subscribeBySupplyNumber: boolean;

  public moduleConfig$ = this.sharedService.moduleConfigModel$.pipe(
    filter(m => m != undefined),
    take(1),
    shareReplay(1)
  );

  constructor(
    protected translator: BiTranslateService,
    protected cd: ChangeDetectorRef,
    protected notifier: BiToastNotificationService,
    protected subscribeModuleService: SubscribeModuleBaseService,
    protected biDialogService: BiDialogBaseService,
    protected sharedService: SubscriptionModuleSharedService
  ) {
    super(translator, cd, notifier, subscribeModuleService, sharedService);
  }

  public ngOnInit() {
    super.ngOnInit();
    this.setTitles();

    this.moduleConfig$.pipe(take(1)).subscribe(() => this.initSubheaderAndRequiredErrorMessage());
  }

  public onReset() {
    super.onReset();
    this.sharedService.subscriptionsInitialized = false;
    this.sharedService.setSubscriberInfo(undefined);
  }

  //#region EVENTHANDLERS
  public onSubscribeClicked(subscribeBySupplyNumber: boolean) {
    this.subscribeBySupplyNumber = subscribeBySupplyNumber;
    this.currentStep.set(3);
  }

  public onUnsubscribe(eventData: UnsubscribeEventData) {
    // First define callback for when user hits the OK button in modal window
    const onActionComplete = () => {
      const unsubMsg = this.translator.instant("iFrame.Subscription.AddressUnsubscribed");
      this.announcer.announce(unsubMsg, "assertive").then(() => {
        this.notifier.createNotification(new BiNotificationConfig(undefined, unsubMsg));
        this.cd.detectChanges();
      });
    };

    const modalCallBack = () => {
      if (eventData.subscription.subscriptionTypeId == SubscriptionType.SupplyNumber) {
        this.sharedService
          .unsubscribeSupplyNumber(eventData.subscription)
          .pipe(untilDestroyed(this), take(1))
          .subscribe({
            complete: () => onActionComplete()
          });
      } else {
        this.sharedService
          .unsubscribeAddress(eventData.subscription)
          .pipe(untilDestroyed(this), take(1))
          .subscribe({
            complete: () => onActionComplete()
          });
      }
    };

    // Determine what is about to be unsubscribed
    let numberOrEmail2Unsubscribe: string;
    if (eventData.subscription.phoneCode && eventData.subscription.phoneNumber)
      numberOrEmail2Unsubscribe = BiPhoneFormatTool.formatPhoneNumber(`+${eventData.subscription.phoneCode}${eventData.subscription.phoneNumber}`);
    else numberOrEmail2Unsubscribe = this.emailCtrl.value;

    // Open modal
    const unsubscribeMessage = this.translator.instant("iFrame.Subscription.UnsubscribeConfirm", {
      phoneOrEmail: numberOrEmail2Unsubscribe,
      address: eventData.subscription.subscriptionTypeId == SubscriptionType.SupplyNumber ? eventData.subscription.locationName.toLowerCase() : eventData.subscription.locationName
    });
    this.biDialogService.openConfirmWarning(unsubscribeMessage, modalCallBack, undefined, undefined, eventData.targetButton);
  }

  public createAddressSubscription(subscriptionEvent: CreateSubscriptionEventData) {
    const dialogOkCallback = () => {
      this.sharedService
        .createAddressSubscription(subscriptionEvent.subscriptionData)
        .pipe(untilDestroyed(this))
        .subscribe({
          complete: () => {
            const messageTranslated = this.translator.instant("shared.SubscriptionDone");
            this.announcer.announce(messageTranslated, "assertive").then(() => {
              this.notifier.createNotification(new BiNotificationConfig(undefined, undefined, messageTranslated));

              this.showSubscribeConfirmedText.set(true);
              this.backToOverview();
            });
          }
        });
    };

    const what2Subscribe = subscriptionEvent.subscriptionData.displayValue;

    this.biDialogService.openConfirm(
      this.translator.instant("iFrame.Subscription.SubscribeConfirm", {
        whatToSubscribe: what2Subscribe
      }),
      dialogOkCallback,
      undefined,
      undefined,
      undefined,
      subscriptionEvent.targetButton
    );
  }

  public createSupplyNumberSubscription(subscriptionData: SupplyNumberAndAlias) {
    const modalOkCallback = () => {
      this.sharedService
        .createSupplyNumberSubscription(subscriptionData)
        .pipe(untilDestroyed(this), take(1))
        .subscribe({
          error: err => {
            if (err.status === 404) {
              const supplyNumberLabel = !isStringNullOrEmpty(this.sharedService.getCachedModuleConfigObject().supplyNumberText)
                ? this.sharedService.getCachedModuleConfigObject().supplyNumberText
                : this.translator.instant("shared.SupplyNumber");
              this.notifier.createNotification(new BiNotificationConfig(NotificationType.WARNING, undefined, this.translator.instant("iframe.Subscription.SupplyNumber404", { supplyNumberLabel })));
            } else console.error(err);
          },
          complete: () => {
            this.showSubscribeConfirmedText.set(true);

            this.notifier.createNotification(new BiNotificationConfig(undefined, this.translator.instant("shared.SubscriptionDone")));
            this.backToOverview();
            this.cd.detectChanges();
          }
        });
    };

    const what2Subscribe = this.sharedService.getSupplyNumberName(subscriptionData.supplyNumber, subscriptionData.alias).toLowerCase();

    this.biDialogService.openConfirm(
      this.translator.instant("iFrame.Subscription.SubscribeConfirm", {
        whatToSubscribe: what2Subscribe
      }),
      modalOkCallback
    );
  }

  public backToOverview() {
    this.currentStep.set(2);
  }

  //#endregion

  protected initMobileEmailForm() {
    super.initMobileEmailForm();
  }

  private setTitles() {
    // When not using "language" URL parameter, we must set title defined by module config model
    if (isStringNullOrEmpty(this.sharedService.urlParams.language)) {
      this.moduleConfig$
        .pipe(
          map(config => config.headerText),
          take(1)
        )
        .subscribe(title => this.title.set(title));
    } else this.title.set(this.translator.instant("administration.subscribeUnsubscribe.DefaultHeaderInIFrame"));
  }

  /**
   * Initializes the subheader text and the error message for "at least 1 input required"-error message used
   * for mobile, email and landline.
   */
  private initSubheaderAndRequiredErrorMessage() {
    if (!this.sharedService.urlParams.hideEmailInput && !this.sharedService.urlParams.showLandline) {
      // Only mobile and email
      this.subHeaderTranslationKey.set("iFrame.Subscription.EnterPhoneOrEmail");
      this.atLeast1RequiredMessage = "errorMessages.MobileAndOrEmailRequired";
    } else if (!this.sharedService.urlParams.hideEmailInput && this.sharedService.urlParams.showLandline) {
      // Both mobile, e-mail and landline
      this.subHeaderTranslationKey.set("iFrame.Subscription.EnterPhoneLandlineAndOrEmail");
      this.atLeast1RequiredMessage = "errorMessages.MobileLandlineAndOrEmailRequired";
    } else if (this.sharedService.urlParams.hideEmailInput && this.sharedService.urlParams.showLandline) {
      // Only mobile and landline
      this.subHeaderTranslationKey.set("iFrame.Subscription.EnterPhoneOrLandline");
      this.atLeast1RequiredMessage = "errorMessages.MobileAndOrLandlineRequired";
    } else if (this.sharedService.urlParams.hideEmailInput) {
      // Only mobile
      this.subHeaderTranslationKey.set("iFrame.Subscription.EnterPhone");
      this.atLeast1RequiredMessage = "errorMessages.MobileRequired";
    }
  }
}
